import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { executionStore } from '@/stores/ExecutionStore'
import { agentStore } from '@/stores/AgentStore'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import Pagination from '@/components/common/Pagination'
import { formatDistanceToNow } from 'date-fns'
import {
  PlayIcon,
  StopIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  FunnelIcon,
  ArrowPathIcon,
  ChartBarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const ExecutionsPage = observer(() => {
  const [selectedExecution, setSelectedExecution] = useState<string | null>(null)

  useEffect(() => {
    executionStore.fetchExecutions()
    agentStore.fetchAgents()
    executionStore.subscribeToExecutionUpdates()

    return () => {
      executionStore.unsubscribeFromExecutionUpdates()
    }
  }, [])

  useEffect(() => {
    executionStore.fetchExecutions()
  }, [executionStore.currentPage, executionStore.pageSize])

  const handleCancelExecution = async (id: string) => {
    if (window.confirm('Are you sure you want to cancel this execution?')) {
      try {
        await executionStore.cancelExecution(id)
        toast.success('Execution cancelled')
      } catch (error) {
        toast.error('Failed to cancel execution')
      }
    }
  }

  const handleDeleteExecution = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this execution?')) {
      try {
        await executionStore.deleteExecution(id)
        toast.success('Execution deleted')
      } catch (error) {
        toast.error('Failed to delete execution')
      }
    }
  }

  const handleRefresh = () => {
    executionStore.fetchExecutions()
    toast.success('Refreshed')
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-600" />
      case 'running':
        return <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin" />
      case 'queued':
        return <ClockIcon className="w-5 h-5 text-yellow-600" />
      case 'cancelled':
        return <StopIcon className="w-5 h-5 text-muted-foreground" />
      default:
        return <ClockIcon className="w-5 h-5 text-muted-foreground" />
    }
  }

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'primary' | 'success' | 'warning' | 'gray'> = {
      running: 'primary',
      completed: 'success',
      failed: 'warning',
      queued: 'gray',
      cancelled: 'gray',
    }
    return variants[status] || 'gray'
  }

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A'
    const seconds = Math.floor(ms / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const totalPages = Math.ceil(executionStore.totalExecutions / executionStore.pageSize)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Executions ({executionStore.totalExecutions} total)
          </h1>
          <p className="text-muted-foreground mt-1">Monitor and manage all agent executions</p>
        </div>
        <Button variant="secondary" onClick={handleRefresh}>
          <ArrowPathIcon className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Running</p>
              <p className="text-2xl font-bold text-blue-600">
                {executionStore.runningExecutions.length}
              </p>
            </div>
            <div className="p-3 bg-blue-500/15 rounded-lg">
              <ArrowPathIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold text-green-600">
                {executionStore.completedExecutions.length}
              </p>
            </div>
            <div className="p-3 bg-green-500/15 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Failed</p>
              <p className="text-2xl font-bold text-red-600">
                {executionStore.failedExecutions.length}
              </p>
            </div>
            <div className="p-3 bg-red-500/15 rounded-lg">
              <XCircleIcon className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold text-foreground">
                {executionStore.successRate}%
              </p>
            </div>
            <div className="p-3 bg-purple-500/15 rounded-lg">
              <ChartBarIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search executions..."
              value={executionStore.searchQuery}
              onChange={(e) => executionStore.setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-4">
            <select
              value={executionStore.statusFilter}
              onChange={(e) =>
                executionStore.setStatusFilter(
                  e.target.value as 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'all'
                )
              }
              className="px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="queued">Queued</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={executionStore.agentFilter}
              onChange={(e) => executionStore.setAgentFilter(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Agents</option>
              {agentStore.agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </select>

            {executionStore.hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={() => executionStore.clearFilters()}>
                <FunnelIcon className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Executions List */}
      {executionStore.isLoading && executionStore.executions.length === 0 ? (
        <Card className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading executions...</p>
        </Card>
      ) : executionStore.filteredExecutions.length === 0 ? (
        <Card className="text-center py-12">
          <PlayIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No executions found</h3>
          <p className="text-muted-foreground">
            {executionStore.hasActiveFilters
              ? 'Try adjusting your filters'
              : 'Execute an agent to see results here'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {executionStore.filteredExecutions.map((execution) => {
            const agent = agentStore.agents.find((a) => a.id === execution.agent_id)

            return (
              <div
                key={execution.id}
                onClick={() => setSelectedExecution(execution.id)}
                className="cursor-pointer"
              >
                <Card
                  className={clsx(
                    'hover:shadow-lg transition-shadow',
                    selectedExecution === execution.id && 'ring-2 ring-primary-500'
                  )}
                >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(execution.status)}
                      <Badge variant={getStatusBadgeVariant(execution.status)}>
                        {execution.status}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(execution.created_at), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-muted-foreground">Agent: </span>
                        <span className="text-sm font-medium text-foreground">
                          {agent?.name || execution.agent_id}
                        </span>
                      </div>

                      <div>
                        <span className="text-sm text-muted-foreground">Execution ID: </span>
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {execution.id}
                        </code>
                      </div>

                      {execution.duration_ms && (
                        <div>
                          <span className="text-sm text-muted-foreground">Duration: </span>
                          <span className="text-sm font-medium text-foreground">
                            {formatDuration(execution.duration_ms)}
                          </span>
                        </div>
                      )}

                      {execution.token_usage && (
                        <div>
                          <span className="text-sm text-muted-foreground">Tokens: </span>
                          <span className="text-sm font-medium text-foreground">
                            {execution.token_usage.total_tokens.toLocaleString()}
                          </span>
                          <span className="text-xs text-muted-foreground ml-2">
                            ({execution.token_usage.prompt_tokens} prompt +{' '}
                            {execution.token_usage.completion_tokens} completion)
                          </span>
                        </div>
                      )}

                      {execution.cost && (
                        <div>
                          <span className="text-sm text-muted-foreground">Cost: </span>
                          <span className="text-sm font-medium text-foreground">
                            ${execution.cost.toFixed(4)}
                          </span>
                        </div>
                      )}

                      {execution.error_message && (
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mt-2">
                          <p className="text-sm text-red-600">
                            <span className="font-semibold">Error: </span>
                            {execution.error_message}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedExecution(execution.id)
                      }}
                    >
                      <EyeIcon className="w-4 h-4" />
                    </Button>

                    {(execution.status === 'running' || execution.status === 'queued') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCancelExecution(execution.id)
                        }}
                      >
                        <StopIcon className="w-4 h-4 text-orange-600" />
                      </Button>
                    )}

                    {(execution.status === 'completed' ||
                      execution.status === 'failed' ||
                      execution.status === 'cancelled') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteExecution(execution.id)
                        }}
                      >
                        <TrashIcon className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
              </div>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={executionStore.currentPage}
          totalPages={totalPages}
          onPageChange={(page) => executionStore.setPage(page)}
        />
      )}

      {/* Execution Detail Modal */}
      {selectedExecution && (
        <ExecutionDetailModal
          executionId={selectedExecution}
          onClose={() => setSelectedExecution(null)}
        />
      )}
    </div>
  )
})

// Execution Detail Modal Component
const ExecutionDetailModal = observer(
  ({ executionId, onClose }: { executionId: string; onClose: () => void }) => {
    const [execution, setExecution] = useState<any>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
      const loadExecution = async () => {
        setIsLoading(true)
        const result = await executionStore.fetchExecutionById(executionId)
        setExecution(result)
        setIsLoading(false)
      }
      loadExecution()
    }, [executionId])

    if (isLoading) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-muted-foreground mt-4">Loading execution details...</p>
            </div>
          </Card>
        </div>
      )
    }

    if (!execution) return null

    const getStatusBadgeVariant = (status: string) => {
      const variants: Record<string, 'primary' | 'success' | 'warning' | 'gray'> = {
        running: 'primary',
        completed: 'success',
        failed: 'warning',
        queued: 'gray',
        cancelled: 'gray',
      }
      return variants[status] || 'gray'
    }

    const formatDuration = (ms?: number) => {
      if (!ms) return 'N/A'
      const seconds = Math.floor(ms / 1000)
      if (seconds < 60) return `${seconds}s`
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}m ${remainingSeconds}s`
    }

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">Execution Details</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-muted-foreground transition-colors"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Status */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Status</h3>
              <Badge variant={getStatusBadgeVariant(execution.status)}>
                {execution.status}
              </Badge>
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Execution ID</h3>
                <code className="text-xs bg-muted px-2 py-1 rounded block">
                  {execution.id}
                </code>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Agent ID</h3>
                <code className="text-xs bg-muted px-2 py-1 rounded block">
                  {execution.agent_id}
                </code>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Created</h3>
                <p className="text-sm text-foreground">
                  {new Date(execution.created_at).toLocaleString()}
                </p>
              </div>
              {execution.completed_at && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Completed</h3>
                  <p className="text-sm text-foreground">
                    {new Date(execution.completed_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Input */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Input</h3>
              <pre className="bg-muted/50 border border-border rounded-lg p-4 text-sm overflow-x-auto">
                {JSON.stringify(execution.input, null, 2)}
              </pre>
            </div>

            {/* Output */}
            {execution.output && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Output</h3>
                <pre className="bg-muted/50 border border-border rounded-lg p-4 text-sm overflow-x-auto">
                  {JSON.stringify(execution.output, null, 2)}
                </pre>
              </div>
            )}

            {/* Error */}
            {execution.error_message && (
              <div>
                <h3 className="text-sm font-semibold text-red-600 mb-2">Error Message</h3>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <p className="text-sm text-red-600">{execution.error_message}</p>
                </div>
              </div>
            )}

            {/* Metrics */}
            {(execution.duration_ms || execution.token_usage || execution.cost) && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Metrics</h3>
                <div className="grid grid-cols-3 gap-4">
                  {execution.duration_ms && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                      <p className="text-xs text-blue-600 mb-1">Duration</p>
                      <p className="text-lg font-semibold text-blue-600">
                        {formatDuration(execution.duration_ms)}
                      </p>
                    </div>
                  )}
                  {execution.token_usage && (
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                      <p className="text-xs text-green-600 mb-1">Tokens</p>
                      <p className="text-lg font-semibold text-green-600">
                        {execution.token_usage.total_tokens.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {execution.cost && (
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                      <p className="text-xs text-purple-600 mb-1">Cost</p>
                      <p className="text-lg font-semibold text-purple-600">
                        ${execution.cost.toFixed(4)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </Card>
        </div>
      </div>
    )
  }
)

export default ExecutionsPage
